import { Component, Prop, h, Host, Event, EventEmitter, Element, Listen, State } from '@stencil/core';
import { close_small, search } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-marketweb-search',
  styleUrl: 'pn-marketweb-search.scss',
  shadow: true,
})
export class PnMarketwebSearch {
  @Prop() disabled: boolean = false;
  @Prop() placeholder: string = null;
  @Prop() inputid: string = null;
  @Prop() name: string = null;
  @Prop() autocomplete: string = null;
  @Prop() list: string = null;
  @Prop() value: string = null;
  /** Label for the button */
  @Prop() label: string = null;
  /** Loading animation */
  @Prop() loading: boolean = false;
  /** Button type, options are 'none' for no button, 'icon' for a labelless button with just a search icon and 'icon-inline' for a search button inside of the search field (this last option disables the clear field button) */
  @Prop() button: string = '';
  /** Light instead of dark search button */
  @Prop() light: boolean = false;

  @Element() hostElement: HTMLElement;

  /** This is emitted on search submission both with keyboard and mouse */
  @Event() search: EventEmitter;
  /** custom event that handles both clearing and input to have the option of just binding listeners to one event instead of two. */
  @Event() update: EventEmitter;

  @Listen('input')
  inputHandler() {
    this.update.emit(this.value);
  }

  emitSearch(e) {
    if (e.type === 'click' || (e.type === 'keydown' && e.key === 'Enter')) {
      // We prevent the native search event since it's not supported in IE and FF, then we emit our own instead
      e.preventDefault();
      this.search.emit(this.value);
    }
  }

  setVal(e) {
    this.value = e.target.value;
  }

  clearInput(e) {
    this.value = '';
    this.update.emit(this.value);
    const closestInputField = e.target.closest("div.input-container")?.querySelector("input[type=search]");
    closestInputField?.focus();
  }

  getClassNames() {
    let classNames = '';

    const buttonTypes = ['none', 'icon', 'icon-inline'];
    if (this.value) classNames += 'searching ';
    if (this.loading) classNames += 'loading ';
    if (buttonTypes.includes(this.button)) classNames += `button-${this.button} `;

    return classNames;
  }
  @State() suggestionObserver: MutationObserver = null;
  @State() hasClonedInput: boolean = false;
  @State() listSuggestion: string[] = [];
  componentWillRender() {
    this.cloneListContent();
    this.autocompleteEventBinding();
  }

  onSuggestionUpdate() {
    this.cloneListContent();
  }

  autocompleteEventBinding() {
    if (this.suggestionObserver !== null || !this.list || !this.hostElement || !document.getElementById(this.list)) {
      return;
    }
    const targetNode = document.getElementById(this.list);
    this.suggestionObserver = new MutationObserver(this.onSuggestionUpdate.bind(this));
    this.suggestionObserver.observe(targetNode, { attributes: true, childList: true, subtree: true });
  }

  cloneListContent() {
    if (!this.list || !this.hostElement || !document.getElementById(this.list)) {
      return;
    }
    const options = [...document.getElementById(this.list).querySelectorAll('option')];
    this.listSuggestion = options.map((item: HTMLOptionElement) => {
      if (item.hasAttribute('value')) {
        return item.getAttribute('value');
      }
      return item.innerText;
    });
  }

  render() {
    return (
      <Host>
        <div class={`input-container ${this.getClassNames()}`} data-haslist={(this.list !== null) + ''}>
          <input
            type="search"
            value={this.value}
            id={this.inputid}
            name={this.name}
            placeholder={this.placeholder}
            disabled={this.disabled}
            autocomplete={this.autocomplete}
            list={this.list}
            onKeyDown={e => this.emitSearch(e)}
            onInput={e => this.setVal(e)}
          />

          <div class="button-container">
            {(this.button === 'none' || this.button === 'icon-inline') && (
              <button class="search" tabindex={this.button === 'icon-inline' ? '0' : '-1'} onClick={e => this.emitSearch(e)} aria-label="search" title="search">
                <pn-icon icon={search} color="blue700"></pn-icon>
              </button>
            )}
            {this.button !== 'icon-inline' && (
              <button
                class="clear"
                aria-label="clear"
                title="clear search field"
                tabindex={this.value && (!this.loading || this.button !== 'none') ? '0' : '-1'}
                onClick={(e) => this.clearInput(e)}
              >
                <pn-icon icon={close_small} color="blue700"></pn-icon>
              </button>
            )}

            {this.button === 'none' || (this.button === 'icon-inline' && <pn-spinner></pn-spinner>)}
          </div>
        </div>

        {this.button !== 'none' && this.button !== 'icon-inline' && (
          <pn-button
            onClick={e => this.emitSearch(e)}
            loading={this.loading}
            icon={search}
            appearance={this.light ? 'light' : 'dark'}
            tooltip={this.button === 'icon' ? this.label : null}
          >
            {this.label}
          </pn-button>
        )}
        {this.list && (
          <datalist id={this.list}>
            {this.listSuggestion.map(item => {
              return <option>{item}</option>;
            })}
          </datalist>
        )}
      </Host>
    );
  }
}
